/**
 * La garde LOCALE de suppression d'une conversation (#1334).
 *
 * Elle n'existait pas. Le parcours de suppression était un DELETE et une
 * invalidation de cache : la connexion temps réel du fil restait ouverte et
 * reconnectable, son document logique gardait sa place de résidence — une des
 * quatre — avec son contrôleur de brouillon, ses timers et ses PUT en vol, et
 * les callbacks de montage continuaient de désigner une session dont la
 * conversation n'existait plus en base.
 *
 * ## Trois temps, et l'ordre est le sujet
 *
 * 1. **Avant** — un tour d'agent ou une opération documentaire PROTÈGE le fil :
 *    on ne supprime pas, on demande l'arrêt. Aucun travail n'est détaché.
 * 2. **Pendant** — le fil est marqué en suppression et aucune nouvelle
 *    opération documentaire ne peut plus l'atteindre. Le marquage est posé
 *    AVANT le DELETE, sinon un tour lancé pendant le trajet réseau
 *    ressusciterait ce qu'on vient de retirer.
 * 3. **Après** — DELETE confirmé : la connexion et ses reprises sont fermées,
 *    le document logique et son montage sont retirés, les callbacks invalidés.
 *    **Un échec ne retire rien** : le fil et son document restent tels quels,
 *    et le marquage est levé pour que l'avocat puisse réessayer.
 *
 * ## Ce que cette garde ne promet PAS
 *
 * Ni coordination entre onglets (un autre onglet du même compte garde son
 * document jusqu'à son propre rafraîchissement), ni effacement des fichiers S3
 * du brouillon. Les deux sont hors de cette tranche, et le dire ici évite qu'on
 * les croie acquis.
 */

import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { estEnCoursDeSuppression } from './filsEnSuppression';
import { isCanvasSessionProtectedByWork } from './canvasSessionProtection';
import { redactionStreamHub } from '../streaming/redactionStreamHub';

/**
 * Le verdict, en union exhaustive — jamais un booléen.
 *
 * « Protégé » et « déjà en cours » n'appellent pas la même suite : le premier
 * demande d'arrêter un travail, le second de ne rien faire du tout.
 */
export type DecisionDeSuppression =
  | { readonly geste: 'supprimer' }
  | { readonly geste: 'attendre-la-fin-du-travail'; readonly raison: string }
  | { readonly geste: 'deja-en-cours' };

export interface EtatDeProtectionDuFil {
  /** Une rédaction écoute encore pour ce fil (`redactionStreamHub`). */
  readonly uneRedactionEstEnCours: boolean;
  /** Le document de ce fil est protégé par un pin, un stream, un writer ou une mutation. */
  readonly leDocumentEstProtege: boolean;
  /**
   * Une transaction de RÉSIDENCE est en vol sur ce document — montage en cours
   * d'hydratation, ou checkpoint de victime LRU en cours.
   *
   * ⚠️ Distinct de `leDocumentEstProtege` : `isCanvasSessionProtected` ne lit
   * que les quatre compteurs de travail, et une session en hydratation ou en
   * checkpoint n'en porte aucun. Supprimer là disposerait le contrôleur au
   * milieu de son PUT, ferait rejeter `checkpointAndCool` et **l'ouverture du
   * document d'une AUTRE conversation** échouerait avec un toast (revue #1334).
   */
  readonly uneTransactionDeResidenceEstEnVol: boolean;
  /** Le fil est déjà dans la fenêtre de suppression. */
  readonly suppressionDejaEnCours: boolean;
}

export const REFUS_SUPPRESSION_TRAVAIL_EN_COURS =
  'Hector rédige encore dans cette conversation. Arrêtez la rédaction depuis le bandeau, '
  + 'ou attendez sa fin, puis supprimez la conversation.';

/**
 * Décision PURE : aucun store, aucun réseau, aucun effet. Elle est testable
 * seule, et c'est ce qui permet de garder ses trois issues sans monter le
 * runtime.
 */
export function deciderDeLaSuppression(etat: EtatDeProtectionDuFil): DecisionDeSuppression {
  if (etat.suppressionDejaEnCours) return { geste: 'deja-en-cours' };
  if (
    etat.uneRedactionEstEnCours
    || etat.leDocumentEstProtege
    || etat.uneTransactionDeResidenceEstEnVol
  ) {
    return { geste: 'attendre-la-fin-du-travail', raison: REFUS_SUPPRESSION_TRAVAIL_EN_COURS };
  }
  return { geste: 'supprimer' };
}

/** Relève l'état de protection réel du fil, pour le passer à la décision pure. */
export function lireLaProtectionDuFil(conversationId: string): EtatDeProtectionDuFil {
  // Un seul relevé, sur une seule clé (#1336) : la conversation. Le double
  // aller-retour d'avant — traduire le fil en identifiant de document, puis
  // retrouver la session par cet identifiant — ne posait qu'une fois la même
  // question à deux collections indexées de la même façon.
  const session = canvasDocumentRuntime.getSessionsSnapshot().sessions
    .find((candidate) => candidate.conversationId === conversationId) ?? null;
  return {
    uneRedactionEstEnCours: redactionStreamHub.ecouteEnCoursPour(conversationId),
    leDocumentEstProtege: isCanvasSessionProtectedByWork(conversationId),
    uneTransactionDeResidenceEstEnVol: session !== null
      && (session.temperature === 'hydrating' || session.checkpoint === 'checkpointing'),
    suppressionDejaEnCours: estEnCoursDeSuppression(conversationId),
  };
}

export interface RessourcesDUnFil {
  /** Ferme la connexion temps réel du fil ET ses reprises programmées. */
  readonly fermerLaConnexion: (conversationId: string) => void;
}

/**
 * Retire les ressources du fil APRÈS un DELETE confirmé.
 *
 * L'ordre reprend celui de la purge de compte (`canvasEviction`), et pour la
 * même raison : disposer le contrôleur de brouillon EN PREMIER invalide ses
 * timers, sa file de PUT et les ACK déjà partis ; détruire le scope d'abord
 * laisserait un export en vol écrire sous une identité qui n'a plus de fil.
 * La suppression de la session du registre vient en dernier : elle est ce qui
 * fait répondre `session-not-found` à tout callback de montage tardif.
 */
export function libererLesRessourcesDuFil(
  conversationId: string,
  ressources: RessourcesDUnFil,
): void {
  // ⚠️ **La connexion d'abord, et pas pour la raison qu'on croit** (revue
  // #1334). La résurrection est déjà interdite par le marquage, posé avant le
  // réseau. La vraie raison est que `WebSocketPool.cleanup` MUTE l'entrée de
  // registre que le retrait ci-dessous s'apprête à supprimer : il rend les pins
  // de tour et les admissions documentaires du fil. Inverser l'ordre rendrait
  // ces libérations muettes sur une session déjà disparue.
  ressources.fermerLaConnexion(conversationId);
  canvasDocumentRuntime.discardSessionForConversation(
    conversationId,
    `conversation ${conversationId} supprimée par l'avocat`,
  );
}
